/**
 * Moving an item between the cloud and this device — the USER's action, and
 * only the user's (cards C3/C6; the rules are in `./itemLocation`).
 *
 * Split out of `itemLocation.js`, `documentsStore.js` and `boardsStore.js` on
 * 17.09.2026. Those three are inside the MCP import graph — the stores ARE the
 * documents and boards surfaces — so while the move lived in them,
 * `records_move_to_device` and `records_move_to_cloud` were one import away
 * from any MCP handler. A move is a data-residency change: it deletes the
 * server copy, or uploads something the user kept off the server. That is the
 * same class as `moveBoardToTeam`, which is out for MCP at any grant level.
 *
 * So this module is imported by UI components and by nothing else.
 * `mcpAttackSurface.test.js` enforces it twice: the two commands are not on the
 * MCP allow-list (so reaching this file from the graph fails the perimeter
 * test), and no `src/mcp` module may name these functions.
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { notePlanLimitRefusal } from '../store/planLimitStore';

// The record `extension_id` of each store (`EXTENSION_ID` in documentsStore.js
// and boardsStore.js). Repeated here rather than imported so this file does not
// pull the stores in; `cloudLocalItems.test.js` pins both against the command.
const DOCUMENTS_EXTENSION_ID = 'documents';
const BOARDS_EXTENSION_ID = 'task-manager';

function currentUserId() {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) throw new Error('signed_out');
  return String(user.id);
}

/**
 * Move an item (board / top-level document) from the cloud to this device.
 * Resolves `{ moved }`. The server copy is deleted by the next sync cycle, so
 * the item disappears from the web app and other devices.
 */
export async function moveItemToDevice({ extensionId, rootId }) {
  if (!isTauri()) throw new Error('desktop_only');
  return invoke('records_move_to_device', { userId: currentUserId(), extensionId, rootId: String(rootId) });
}

/**
 * Move an item from this device to the cloud. Resolves `{ moved }` on success
 * or `{ moved: 0, refused }` when the server refused it (e.g.
 * `plan_quota_boards`) — nothing changed then. Rejects when offline.
 */
export async function moveItemToCloud({ extensionId, rootId }) {
  if (!isTauri()) throw new Error('desktop_only');
  const result = await invoke('records_move_to_cloud', { userId: currentUserId(), extensionId, rootId: String(rootId) });
  // Refused by a plan limit: the titlebar sync menu says so, with the usage bars.
  if (result?.refused) notePlanLimitRefusal(result.refused);
  return result;
}

/**
 * "Download to this device" (Plans v2 card 3.4): a board or top-level document
 * of a READ-ONLY cloud (a plan without `cloud_sync`) comes to this device,
 * keeping its ids, where it can be edited again. The cloud copy is left exactly
 * as it is: nothing is queued for the server, nothing is deleted. Resolves
 * `{ copied }`. Works offline.
 */
export async function copyItemToDevice({ extensionId, rootId }) {
  if (!isTauri()) throw new Error('desktop_only');
  return invoke('records_copy_to_device', { userId: currentUserId(), extensionId, rootId: String(rootId) });
}

export const copyDocumentToDevice = (docId) => copyItemToDevice({ extensionId: DOCUMENTS_EXTENSION_ID, rootId: docId });
export const copyBoardToDevice = (boardId) => copyItemToDevice({ extensionId: BOARDS_EXTENSION_ID, rootId: boardId });

/**
 * Every board and document of the account's cloud to this device, cloud copy
 * untouched (owner decision 16.10.2026 for old Free cloud data). Resolves
 * `{ copied }`; 0 when the cloud scope holds nothing.
 */
export async function copyCloudItemsToDevice() {
  if (!isTauri()) throw new Error('desktop_only');
  return invoke('records_copy_cloud_to_device', { userId: currentUserId() });
}

/**
 * Plans v2: the plan has `cloud_sync` (again, or for the first time). Ops the
 * server refused with a 402 plan refusal while it had none (calendar,
 * projects, links of the cloud scope) were parked by the engine; they go back
 * into the outbox and sync. Ops of records since moved or copied to this
 * device stay parked. Resolves `{ unparked }`; 0 when nothing was parked.
 */
export async function unparkPlanRefusedOps() {
  if (!isTauri()) throw new Error('desktop_only');
  return invoke('records_unpark_plan_refused', { userId: currentUserId() });
}

/**
 * Move a TOP-LEVEL document (with its subpages and bodies) between the cloud
 * and this device — card C6. All or nothing: the server takes every record or
 * none, so a refusal by the Free document limit changes nothing anywhere and
 * is reported through the titlebar limits panel.
 */
export function moveDocumentToDevice(docId) {
  return moveItemToDevice({ extensionId: DOCUMENTS_EXTENSION_ID, rootId: docId });
}

export function moveDocumentToCloud(docId) {
  return moveItemToCloud({ extensionId: DOCUMENTS_EXTENSION_ID, rootId: docId });
}

/**
 * Free plan, cloud/local items: move a private board to this device. Resolves
 * `{ moved }`. It disappears from the web app and other devices.
 */
export function moveBoardToDevice(boardId) {
  return moveItemToDevice({ extensionId: BOARDS_EXTENSION_ID, rootId: boardId });
}

/**
 * Move a board from this device to the cloud. Resolves `{ moved }`, or
 * `{ moved: 0, refused }` when the plan limit refused it (nothing changed).
 */
export function moveBoardToCloud(boardId) {
  return moveItemToCloud({ extensionId: BOARDS_EXTENSION_ID, rootId: boardId });
}
